import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const ProductCreateModal = ({ show, onClose, onSubmit }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState({
        product_name: '',
        model_number: '',
        description: '',
        release_year: '',
        warranty_duration: '',
        warranty_coverage: '',
        documentation_url: '',
        initial_price: '',
    });
    const [saving, setSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await onSubmit(form);
            setForm({
                product_name: '',
                model_number: '',
                description: '',
                release_year: '',
                warranty_duration: '',
                warranty_coverage: '',
                documentation_url: '',
                initial_price: '',
            });
        } catch (error) {
            toast.error(t('ManufacturerProducts.errors.createFailed'));
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    return (
        <Overlay>
            <Modal>
                <Header>
                    <h3>{t('ManufacturerProducts.create.title')}</h3>
                    <CloseButton onClick={onClose}>×</CloseButton>
                </Header>
                <form onSubmit={handleSubmit}>
                    <Grid>
                        <label>
                            {t('ManufacturerProducts.create.fields.product_name')}
                            <input
                                name="product_name"
                                value={form.product_name}
                                onChange={handleChange}
                                required
                            />
                        </label>
                        <label>
                            {t('ManufacturerProducts.create.fields.model_number')}
                            <input
                                name="model_number"
                                value={form.model_number}
                                onChange={handleChange}
                                required
                            />
                        </label>
                        <label>
                            {t('ManufacturerProducts.create.fields.release_year')}
                            <input
                                name="release_year"
                                type="number"
                                value={form.release_year}
                                onChange={handleChange}
                            />
                        </label>
                        <label>
                            {t('ManufacturerProducts.create.fields.initial_price')}
                            <input
                                name="initial_price"
                                type="number"
                                step="0.01"
                                value={form.initial_price}
                                onChange={handleChange}
                            />
                        </label>
                    </Grid>
                    <label>
                        {t('ManufacturerProducts.create.fields.description')}
                        <textarea
                            name="description"
                            rows="3"
                            value={form.description}
                            onChange={handleChange}
                        />
                    </label>
                    <Grid>
                        <label>
                            {t('ManufacturerProducts.create.fields.warranty_duration')}
                            <input
                                name="warranty_duration"
                                type="number"
                                value={form.warranty_duration}
                                onChange={handleChange}
                            />
                        </label>
                        <label>
                            {t('ManufacturerProducts.create.fields.warranty_coverage')}
                            <input
                                name="warranty_coverage"
                                value={form.warranty_coverage}
                                onChange={handleChange}
                            />
                        </label>
                        <label>
                            {t('ManufacturerProducts.create.fields.documentation_url')}
                            <input
                                name="documentation_url"
                                value={form.documentation_url}
                                onChange={handleChange}
                            />
                        </label>
                    </Grid>
                    <Footer>
                        <button type="button" onClick={onClose}>
                            {t('ManufacturerProducts.create.cancel')}
                        </button>
                        <button type="submit" disabled={saving}>
                            {saving ? t('ManufacturerProducts.create.saving') : t('ManufacturerProducts.create.save')}
                        </button>
                    </Footer>
                </form>
            </Modal>
        </Overlay>
    );
};

export default ProductCreateModal;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1100;
`;

const Modal = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 1.25rem;
    width: 600px;
    max-width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    h3 {
        margin: 0;
        color: rgba(130, 22, 146, 1);
    }
`;

const CloseButton = styled.button`
    border: none;
    background: transparent;
    font-size: 1.5rem;
    cursor: pointer;
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.75rem;
    label {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
    }
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;

    button {
        padding: 0.625rem 1rem;
        border-radius: 8px;
        border: none;
        cursor: pointer;
    }

    button[type='button'] {
        background: rgba(130, 22, 146, 0.1);
        color: rgba(130, 22, 146, 1);
    }

    button[type='submit'] {
        background: rgba(130, 22, 146, 1);
        color: #fff;
    }
`;
